import type { ListBuilderMobileCardConfig } from '../../types';

/**
 * Required fields for shop_default list (all columns including sales/invoice data)
 */
export const SHOP_DEFAULT_REQUIRED_FIELDS = [
	'_id',
	'primaryImageUrl',
	'category',
	'description',
	'externalId',
	'externalId2',
	'isShortDate',
	'isHazmat',
	'isRefrigerated',
	'manufacturer',
	'minimumPrice',
	'price',
	'quantityOnHand',
	'quantityReserved',
	'quantityOnOrder',
	'sizeCount',
	'soonestExpirationDate',
	'subcategory',
	'unitOfMeasure',
	'addressSoldUnitsMtd',
	'addressSoldUnitsLastMonth',
	'addressSoldUnitsTwoMonthsAgo',
	'lastInvoiceId',
	'addressLastPrice',
	'lastInvoiceExternalId',
	'lastInvoiceDate',
];

/**
 * Required fields for shop_suggested (reorder) list - includes forecast fields
 */
export const SHOP_SUGGESTED_REQUIRED_FIELDS = [
	'client_shop._id',
	'client_shop.primaryImageUrl',
	'addressSoldUnitsMtd',
	'addressSoldUnitsLastMonth',
	'addressSoldUnitsTwoMonthsAgo',
	'lastInvoiceId',
	'addressLastPrice',
	'lastInvoiceExternalId',
	'lastInvoiceDate',
	'addressForecastedReorderDate',
	'addressForecast',
	'item._id',
	'item.category',
	'item.description',
	'item.externalId',
	'item.isShortDate',
	'item.isHazmat',
	'item.isRefrigerated',
	'item.manufacturer',
	'item.minimumPrice',
	'item.price',
	'item.quantityOnHand',
	'item.quantityReserved',
	'item.quantityOnOrder',
	'item.sizeCount',
	'item.soonestExpirationDate',
	'item.subcategory',
	'item.unitOfMeasure',
];

/**
 * Required fields for at_risk, previously_purchased, short_date lists
 */
export const SHOP_STANDARD_REQUIRED_FIELDS = [
	'client_shop._id',
	'client_shop.primaryImageUrl',
	'addressSoldUnitsMtd',
	'addressSoldUnitsLastMonth',
	'addressSoldUnitsTwoMonthsAgo',
	'lastInvoiceId',
	'addressLastPrice',
	'lastInvoiceExternalId',
	'lastInvoiceDate',
	'item._id',
	'item.category',
	'item.description',
	'item.externalId',
	'item.isShortDate',
	'item.isHazmat',
	'item.isRefrigerated',
	'item.manufacturer',
	'item.minimumPrice',
	'item.price',
	'item.quantityOnHand',
	'item.quantityReserved',
	'item.quantityOnOrder',
	'item.sizeCount',
	'item.soonestExpirationDate',
	'item.subcategory',
	'item.unitOfMeasure',
];

/**
 * Required fields for the Recently Restocked tab — standard set plus
 * lastRestockedDate (the column displayed in this tab).
 */
export const SHOP_RECENTLY_RESTOCKED_REQUIRED_FIELDS = [
	...SHOP_STANDARD_REQUIRED_FIELDS,
	'item.lastRestockedDate',
];

/**
 * Required fields for the Newly Stocked tab — standard set plus the two
 * fields the computed created-date column derives from.
 */
export const SHOP_NEWLY_STOCKED_REQUIRED_FIELDS = [
	...SHOP_STANDARD_REQUIRED_FIELDS,
	'item.externalCreatedDate',
	'item._created_date',
];

/**
 * Required fields for purchased_together list (minimal fields)
 */
export const SHOP_PURCHASED_TOGETHER_REQUIRED_FIELDS = [
	'client_shop._id',
	'client_shop.primaryImageUrl',
	'item._id',
	'item.category',
	'item.description',
	'item.externalId',
	'item.isShortDate',
	'item.isHazmat',
	'item.isRefrigerated',
	'item.manufacturer',
	'item.minimumPrice',
	'item.price',
	'item.quantityOnHand',
	'item.quantityReserved',
	'item.quantityOnOrder',
	'item.sizeCount',
	'item.soonestExpirationDate',
	'item.subcategory',
	'item.unitOfMeasure',
];

/**
 * Meta chips a shop card may carry. Reps quote and order from a phone using
 * the same columns they configured for the desktop table (last invoice, last
 * price, cost, monthly units), so the shop lists every one of them rather
 * than taking the engine's default cap. Axia's saved list comes to 17 chips
 * and the Reorder tab has two more than the standard set; this leaves room for
 * a tenant to add columns without a chip silently disappearing.
 */
export const SHOP_MAX_META_PAIRS = 30;

/**
 * Standard mobile card config for shop lists, on the zones engine
 * (PD-13292 follow-up). Name/manufacturer stay the title block, the Target
 * price renders as the hero metric, and every other configured column flows
 * as inline meta chips with empty values dropped per row, including the
 * last-invoice and monthly-sales stacks. A column only stays off the card by
 * being an image, an action or the qty/price entry, which remains a
 * full-width control at the card bottom.
 *
 * Column ids vary per shop tab (purchased_together uses column_price), so
 * refs that miss simply no-op and the displayStyle defaults take over —
 * that keeps this one config safe across every shop list it's attached to.
 */
export const SHOP_MOBILE_CARD_CONFIG: ListBuilderMobileCardConfig = {
	cardLayout: 'zones',
	primaryFields: ['item_details'],
	heroField: 'column_1751545591402.field_1751545620039',
	controlsColumns: ['actions'],
	hiddenFields: ['image'],
	maxMetaPairs: SHOP_MAX_META_PAIRS,
};
